import { useTranslation } from 'react-i18next';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  Box,
  Grid,
  IconButton,
  Typography,
  Badge,
  NumberField,
} from 'ovComponents';
import { mapValues } from 'lodash/fp';
import { round } from 'lodash';
import { AssetClassTargetAllocation } from '../addModelPortfoliosIPSAndAssetClasses.visual';
import { AssetClassSelect } from '../../../../../3-pattern/assetClassSelect/assetClassSelect';
import { Branch } from '../../../../../../interfaces';

export const AssetClassesTargetAllocations = (
  {
    targetAllocations,
    setTargetAllocations,
    isFreeForm = false,
    securities = [],
    testId = 'asset-classes-target-allocations',
  }: {
    targetAllocations: AssetClassTargetAllocation[],
    setTargetAllocations: (targetAllocations: AssetClassTargetAllocation[]) => void,
    isFreeForm?: boolean,
    securities?: Branch[],
    testId?: string,
  },
) => {
  const { t } = useTranslation(['modelPortfolios', 'buildModels']);

  const calculateActualPercentages = () => {
    const allAllocations = securities.reduce((acc: { [key: string]: number }, security: Branch) => {
      const primaryAssetClassId = security.financialProduct?.primaryAssetClass?.id;
      const secondaryAssetClassId = security.financialProduct?.secondaryAssetClass?.id;
      const percentage = security.percentage || 0;

      if (primaryAssetClassId) {
        acc[primaryAssetClassId] = (acc[primaryAssetClassId] || 0) + percentage;
      }
      if (secondaryAssetClassId) {
        acc[secondaryAssetClassId] = (acc[secondaryAssetClassId] || 0) + percentage;
      }
      return acc;
    }, {});

    return mapValues((x: number) => round(x, 2))(allAllocations);
  };

  const actualPercentages = calculateActualPercentages();

  const handleAssetClassChange = (id: string) => (event: any) => {
    const selectedValue = event.target.value;
    // Only update if the selected value is a valid asset class ID
    if (selectedValue && selectedValue !== '') {
      setTargetAllocations(targetAllocations.map((allocation) => (
        allocation.secondaryAssetClass.id === id
          ? { ...allocation, secondaryAssetClass: { id: selectedValue } }
          : allocation
      )));
    }
  };

  const handlePercentageChange = (id: string, field: 'min' | 'max' | 'actualPercentage') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    // Only allow numbers and empty string
    if (value === '' || (/^\d*\.?\d*$/.test(value) && parseFloat(value) <= 100)) {
      setTargetAllocations(targetAllocations.map((allocation) => (
        allocation.secondaryAssetClass.id === id
          ? { ...allocation, [field]: value === '' ? 0 : parseFloat(value) }
          : allocation
      )));
    }
  };

  const addAllocation = () => {
    const newAllocation: AssetClassTargetAllocation = {
      secondaryAssetClass: {
        id: String(Date.now()),
      },
      min: 0,
      max: 0,
      actualPercentage: 0,
      color: 'supportOne',
      fromSecurity: false,
    };
    setTargetAllocations([...targetAllocations, newAllocation]);
  };

  const removeAllocation = (id: string) => {
    setTargetAllocations(targetAllocations.filter((allocation) => allocation.secondaryAssetClass.id !== id));
  };

  const usedAssetClasses = targetAllocations.map((allocation) => allocation.secondaryAssetClass.id).filter(Boolean);

  return (
    <Box sx={{
      border: isFreeForm ? 'none' : '1px solid',
      borderColor: 'divider',
      borderRadius: isFreeForm ? 0 : 1,
      pl: isFreeForm ? 0 : 2,
      pt: isFreeForm ? 0 : 2,
      pb: isFreeForm ? 0 : 1,
    }}>
      {targetAllocations.map((allocation, index) => {
        const assetClassId = allocation.secondaryAssetClass?.id || '';
        const actualPercentage = actualPercentages[assetClassId] || 0;
        const isInBounds = actualPercentage >= allocation.min && actualPercentage <= allocation.max;

        return (
          <Grid
            container
            spacing={1.5}
            key={allocation.secondaryAssetClass.id}
            sx={{
              mb: 2,
              ml: isFreeForm ? -1 : 1.5,
            }}
          >
            <Grid item sx={{
              width: isFreeForm ? 'auto' : '160px',
              flex: isFreeForm ? 1 : 'none',
              minWidth: '160px',
            }}>
              <AssetClassSelect
                testId={`${testId}-asset-class-select-${index}`}
                value={allocation.secondaryAssetClass.id || ''}
                setAssetClass={handleAssetClassChange(allocation.secondaryAssetClass.id || '')}
                label={t('assetClass')}
                filteredAllocations={usedAssetClasses}
                omitAny={true}
                required
              />
            </Grid>
            <Grid item sx={{ width: '110px', flex: 'none' }}>
              <NumberField
                testId={`${testId}-min-input-${index}`}
                label={t('min')}
                number={allocation.min.toString()}
                setNumber={(value) => handlePercentageChange(allocation.secondaryAssetClass.id
                  || '', 'min')({ target: { value } } as React.ChangeEvent<HTMLInputElement>)}
                required
                error={allocation.min < 0 || allocation.min > 100}
                trailingIcon="percent"
                unsigned
              />
            </Grid>
            <Grid item sx={{ width: '110px', flex: 'none' }}>
              <NumberField
                testId={`${testId}-max-input-${index}`}
                label={t('max')}
                number={allocation.max.toString()}
                setNumber={(value) => handlePercentageChange(allocation.secondaryAssetClass.id
                  || '', 'max')({ target: { value } } as React.ChangeEvent<HTMLInputElement>)}
                required
                error={allocation.max < 0 || allocation.max > 100 || allocation.max < allocation.min}
                trailingIcon="percent"
                unsigned
              />
            </Grid>
            <Grid item sx={{ width: '80px', flex: 'none' }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                height: '100%',
              }}>
                <Typography
                  data-testid={`${testId}-actual-percentage-${index}`}
                  variant='labelSmall'
                  sx={{ mb: '4px', color: 'text.secondary' }}
                >
                  {t('buildModels:actual')}
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: '7px',
                }}>
                  <Badge
                    label={`${actualPercentage.toFixed(2)}%`}
                    color={isInBounds ? 'positive' : 'negative'}
                    data-testid={`${testId}-actual-percentage-badge-${index}`}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '14px',
                        fontFamily: 'Interphases Pro',
                        fontWeight: 400,
                        borderRadius: '4px',
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            {targetAllocations.length > 1 && (
              <Grid item sx={{
                width: '40px',
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                mt: 1.5,
                ml: isFreeForm ? -2 : 0,
              }}>
                <IconButton
                  data-testid={`${testId}-remove-allocation-button-${index}`}
                  onClick={() => removeAllocation(allocation.secondaryAssetClass.id || '')}
                  size="small"
                  sx={{
                    color: 'GrayText',
                    padding: '4px',
                  }}
                  aria-label="delete asset class allocation"
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
        );
      })}
      <Typography
        data-testid={`${testId}-add-asset-class-branch-button`}
        sx={{
          fontSize: '12px',
          fontWeight: 500,
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8,
          },
          color: 'primary.main',
          pb: isFreeForm ? 0 : 2,
          display: 'inline-block',
        }}
        onClick={addAllocation}
      >
        {t('addAssetClassBranch')}
      </Typography>
    </Box>
  );
};
